/**
 * <bk-card-gallery>: the photos of a product card change on their own while the card is on screen
 * (owner's note of 2026-09-15: "the photos change on screen, without hover").
 *
 * The frame holds the card's first photos stacked in one place ([data-card-slide]); the first one
 * carries [data-shown] in the markup, so without JavaScript the card simply shows its main photo.
 * Once at least 60% of the card is on screen it waits a full interval, then cross-fades to the next
 * photo every few seconds. Each card keeps its own rhythm (a random start inside a short window), so
 * a grid never flips in sync. It pauses while the pointer or keyboard focus is on the card and while
 * the tab is hidden, and it never moves for a visitor who asks for reduced motion.
 * data-active on the element names the photo shown (0 is the main photo).
 *
 * Only the main photo is in the page's first download: the later ones wait in data-src / data-srcset
 * (snippets/product-card.liquid). The next photo is fetched once the page has finished loading and
 * the card is rotating, a full interval before it is due, and a photo that has not arrived yet is
 * skipped for that turn rather than faded in blank. A card that never rotates (reduced motion, off
 * screen) never downloads them.
 */

/**
 * matches() that answers false instead of throwing on a selector the browser does not know
 * (:focus-visible before Safari 15.4, :modal before Safari 15.6 / Chrome 105).
 * @param {Element | null} element
 * @param {string} selector
 */
function safeMatches(element, selector) {
  try {
    return Boolean(element?.matches(selector));
  } catch {
    return false;
  }
}
const INTERVAL = 3200;
const STAGGER = 1200;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {() => void} callback - Runs once the page has loaded (at once if it already has). */
function whenLoaded(callback) {
  if (document.readyState === 'complete') callback();
  else window.addEventListener('load', () => callback(), { once: true });
}

class BkCardGallery extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];
  #index = 0;
  #timer = 0;
  #visible = false;
  #held = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-card-slide]')].filter((slide) => slide instanceof HTMLElement);
    if (this.#slides.length < 2) return;

    this.#show(0);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const card = this.closest('.product-card') ?? this;
    // Only a mouse holds the photos. A finger is a pointer too: a scroll that starts on the card
    // fires pointerenter, and WebKit often never sends the matching pointerleave, which stopped the
    // photos for the rest of the visit (owner, 2026-09-20: "photo of product are not changing on
    // phone"). Same trap as the hero tour in scene-shop.js.
    card.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') this.#hold(true); }, { signal });
    card.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.#hold(false); }, { signal });
    // Keyboard focus holds them so a visitor reading the card with the keyboard is not interrupted;
    // the focus a tap leaves behind does not (:focus-visible is the keyboard's own signal).
    card.addEventListener('focusin', (event) => {
      const target = event.target;
      if (target instanceof Element && safeMatches(target, ':focus-visible')) this.#hold(true);
    }, { signal });
    card.addEventListener('focusout', () => this.#hold(false), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    reducedMotion.addEventListener('change', () => this.#sync(), { signal });

    // A quarter of the card on screen is enough. 60 % was fine for a grid, where every card in the
    // row is whole, but the home page shows its products in a side-scrolling row: at 390 px the
    // second card is 28 % visible and the ones after it are off screen, so only the first card ever
    // moved (owner, 2026-09-24: "photo not changing on main page"). A card that is entirely off
    // screen still never rotates, and so never downloads its other photos.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.#sync();
      },
      { threshold: [0, 0.25, 1] }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {boolean} held */
  #hold(held) {
    this.#held = held;
    this.#sync();
  }

  #sync() {
    const run = this.#visible && !this.#held && !document.hidden && !reducedMotion.matches;
    if (run && !this.#timer) this.#start();
    else if (!run) this.#stop();
  }

  #start() {
    const tick = () => {
      const next = (this.#index + 1) % this.#slides.length;
      if (this.#ready(this.#slides[next])) this.#show(next);
      this.#prepare((this.#index + 1) % this.#slides.length);
      this.#timer = window.setTimeout(tick, INTERVAL);
    };
    this.#prepare((this.#index + 1) % this.#slides.length);
    this.#timer = window.setTimeout(tick, INTERVAL + Math.round(Math.random() * STAGGER));
  }

  /**
   * Start fetching a waiting photo, after the page's own load so it never competes with it.
   * @param {number} index
   */
  #prepare(index) {
    const slide = this.#slides[index];
    if (!slide || !slide.dataset.src) return;
    whenLoaded(() => {
      if (!slide.dataset.src) return;
      if (slide.dataset.srcset) slide.setAttribute('srcset', slide.dataset.srcset);
      slide.setAttribute('src', slide.dataset.src);
      delete slide.dataset.srcset;
      delete slide.dataset.src;
    });
  }

  /**
   * @param {HTMLElement | undefined} slide
   * @returns {boolean} true when the photo can be shown now
   */
  #ready(slide) {
    if (!(slide instanceof HTMLImageElement)) return Boolean(slide);
    return !slide.dataset.src && slide.complete && slide.naturalWidth > 0;
  }

  #stop() {
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }

  /** @param {number} index */
  #show(index) {
    this.#index = index;
    this.#slides.forEach((slide, position) => slide.toggleAttribute('data-shown', position === index));
    this.setAttribute('data-active', String(index));
  }
}

if (!customElements.get('bk-card-gallery')) customElements.define('bk-card-gallery', BkCardGallery);
